import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Bell, X } from 'lucide-react';
import { relativeTime } from '@homework/shared';
import { useRealtime } from '../lib/realtime';

/**
 * The bell and its list. The list opens on top of everything — drawn at the
 * top of the page rather than inside the top bar, so no screen's own layers
 * can cover it — on a solid white card that reads clearly over any page.
 */
export function NotificationsMenu() {
  const { notifications, dismissNotification } = useRealtime();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<{ top: number; right: number } | null>(null);
  const button = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = button.current?.getBoundingClientRect();
      if (rect) setAt({ top: rect.bottom + 8, right: Math.max(8, window.innerWidth - rect.right) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!button.current?.contains(target) && !panel.current?.contains(target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      setOpen(false);
      button.current?.focus();
    };
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', escape);
    };
  }, [open]);

  return (
    <>
      <button
        ref={button}
        type="button"
        className="btn-ghost relative p-2"
        onClick={() => setOpen((value) => !value)}
        aria-label={`Notifications${notifications.length > 0 ? ` (${notifications.length} unread)` : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
      >
        <Bell className="h-5 w-5" aria-hidden />
        {notifications.length > 0 ? (
          <span className="absolute right-1 top-1 grid h-4 min-w-4 place-items-center rounded-full bg-rose-500 px-1 text-[10px] font-bold text-white">
            {notifications.length > 9 ? '9+' : notifications.length}
          </span>
        ) : null}
      </button>
      {open && at
        ? createPortal(
            <div
              ref={panel}
              role="dialog"
              aria-label="Notifications"
              className="fixed z-[90] w-80 max-w-[calc(100vw-1rem)] animate-slide-up overflow-hidden rounded-3xl bg-white shadow-[0_24px_60px_-12px_rgba(15,23,42,0.35)] ring-1 ring-slate-200"
              style={{ top: at.top, right: at.right }}
            >
              <p className="border-b border-slate-100 px-4 py-3 text-sm font-bold text-slate-900">Notifications</p>
              {notifications.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-500">Nothing needs your attention.</p>
              ) : (
                <ul className="max-h-[min(24rem,70vh)] divide-y divide-slate-100 overflow-y-auto">
                  {notifications.map((notification) => (
                    <li key={notification.id} className="px-4 py-3 hover:bg-slate-50">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          {notification.href ? (
                            <Link to={notification.href} className="text-sm font-semibold text-slate-900 hover:underline" onClick={() => setOpen(false)}>
                              {notification.title}
                            </Link>
                          ) : (
                            <p className="text-sm font-semibold text-slate-900">{notification.title}</p>
                          )}
                          <p className="mt-0.5 text-sm text-slate-600">{notification.body}</p>
                          <p className="mt-1 text-xs text-slate-400">{relativeTime(notification.createdAt)}</p>
                        </div>
                        <button type="button" className="text-slate-400 transition hover:text-slate-600" aria-label="Dismiss" onClick={() => dismissNotification(notification.id)}>
                          <X className="h-4 w-4" aria-hidden />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
